// The viewer's participation in one occurrence, end to end: the request, then
// the announcement every page that shows events applies (eventChanges). Each
// result announced is the server's confirmed one. Mirrors iOS
// CalendarEventStore's occurrence RSVP methods.

import { clearOccurrenceRsvp, fetchEvent, fetchOccurrence, setOccurrenceRsvp } from "../api/EventAPI";
import { announceEventChange } from "./eventChanges";
import { errorCode, isNotFound } from "./eventErrors";
import { applyOccurrenceRsvpState, isOccurrenceOnly } from "./occurrenceParticipation";

function announceUpdated(occurrence) {
  announceEventChange({
    kind: "occurrenceUpdated",
    eventId: occurrence.event_id,
    occurrenceId: occurrence.id,
    occurrence,
  });
}

function announceAccessLost(occurrenceId, eventId = null) {
  announceEventChange({ kind: "occurrenceAccessLost", eventId, occurrenceId });
}

// After a rejected change: canceled meanwhile, gone, or not visible. Re-reads
// the occurrence so every page shows which — a 404 drops it, anything else is
// applied. A failed re-read changes nothing.
// → "current" (re-read and applied) | "gone" (404) | "unknown" (failed).
async function reconcile(occurrenceId, token) {
  try {
    announceUpdated(await fetchOccurrence(occurrenceId, token));
    return "current";
  } catch (err) {
    if (!isNotFound(err)) return "unknown";
    announceAccessLost(occurrenceId);
    return "gone";
  }
}

const isRejection = (err) => isNotFound(err) || errorCode(err) === "occurrence_canceled";

// Going / Declined and/or a reminder change for this occurrence only
// (POST /event-occurrences/{id}/rsvp/; `update` as occurrenceRsvpBody). The
// series RSVP is untouched. → { state, occurrence } (the occurrence with the
// confirmed participation). Errors are rethrown.
export async function answerOccurrence(occurrence, update, token) {
  let state;
  try {
    state = await setOccurrenceRsvp(occurrence.id, update, token);
  } catch (err) {
    if (isRejection(err)) await reconcile(occurrence.id, token);
    throw err;
  }
  const updated = applyOccurrenceRsvpState(occurrence, state);
  announceUpdated(updated);
  return { state, occurrence: updated };
}

// An occurrence-only guest's Remove (DELETE /event-occurrences/{id}/rsvp/):
// with no series RSVP to fall back to, the successful DELETE is itself the
// confirmation that they no longer take part — the occurrence is dropped
// everywhere at once, with no follow-up request, so a later network failure
// can't bring it back. A failed DELETE changes nothing locally and is
// rethrown, except a 404 for an occurrence that's gone (the re-read is a 404
// too): the goal is met. → "removed" | "alreadyGone".
export async function removeOccurrenceParticipation(occurrence, token) {
  try {
    await clearOccurrenceRsvp(occurrence.id, token);
  } catch (err) {
    // A 404 is gone, or nothing to remove: the re-read tells which.
    if (isRejection(err) && (await reconcile(occurrence.id, token)) === "gone" && isNotFound(err)) {
      return "alreadyGone";
    }
    throw err;
  }
  announceAccessLost(occurrence.id, occurrence.event_id);
  return "removed";
}

// A series participant's "Use Series RSVP" (the same DELETE): the occurrence
// follows the series RSVP and reminder again and stays visible, so it's
// re-read rather than dropped — the inherited values aren't known here. If the
// re-read fails, pages are told to re-read themselves. → the fresh occurrence,
// or null.
export async function followSeriesRsvp(occurrence, token) {
  try {
    await clearOccurrenceRsvp(occurrence.id, token);
  } catch (err) {
    if (isRejection(err)) await reconcile(occurrence.id, token);
    throw err;
  }
  try {
    const fresh = await fetchOccurrence(occurrence.id, token);
    announceUpdated(fresh);
    return fresh;
  } catch (err) {
    if (isNotFound(err)) announceAccessLost(occurrence.id, occurrence.event_id);
    else announceEventChange({ kind: "updated", eventId: occurrence.event_id });
    return null;
  }
}

// How an occurrence's detail can be shown to the viewer (mirrors iOS
// CalendarEventStore.loadDetailAccess), from whichever of these the opener
// has: `occurrence` (a payload), `occurrenceId` (e.g. a notification target),
// `eventId`.
//   { kind: "event", event, occurrence }  creator or series participant: the
//                                         full Event, at the occurrence
//   { kind: "occurrenceOnly", occurrence } invited to this occurrence only:
//                                         GET /event-occurrences/{id}/ alone
//                                         (with its guest list); the Event
//                                         is never requested
//   { kind: "unavailable" }               gone, or no longer visible
// Other failures are thrown (retryable). An Event 404 isn't proof the
// occurrence is gone — an older payload doesn't say it's occurrence-only — so
// the occurrence decides. No other occurrence is fetched.
export async function loadEventDetailAccess({ eventId = null, occurrence = null, occurrenceId = null }, token) {
  const id = occurrence?.id ?? occurrenceId;
  const occurrenceOnly = async () => {
    const fresh = await fetchOccurrence(id, token);
    return isOccurrenceOnly(fresh) ? { kind: "occurrenceOnly", occurrence: fresh } : { kind: "unavailable" };
  };

  try {
    let current = occurrence;
    let fetched = false;
    if (!current && id != null) {
      current = await fetchOccurrence(id, token);
      fetched = true;
    }
    if (current && isOccurrenceOnly(current)) {
      // A list payload is refreshed (participation and guest list).
      const fresh = fetched ? current : await fetchOccurrence(id, token);
      if (isOccurrenceOnly(fresh)) return { kind: "occurrenceOnly", occurrence: fresh };
      current = fresh; // invited to the series meanwhile
    }

    const resolvedEventId = current?.event_id ?? eventId;
    if (resolvedEventId == null) return { kind: "unavailable" };
    try {
      const event = await fetchEvent(resolvedEventId, token);
      return { kind: "event", event, occurrence: current ?? null };
    } catch (err) {
      if (!isNotFound(err) || id == null) throw err;
      return await occurrenceOnly();
    }
  } catch (err) {
    if (isNotFound(err)) return { kind: "unavailable" };
    throw err;
  }
}
